import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { AvDialog } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { noop } from '@fabric-ui/utils';
import { Box, Button, ButtonProps as MuiButtonProps } from '@mui/material';
import { useMatch } from '@tanstack/react-router';
import { useBlocker } from '@utils/AvRouter';

export interface NavigationBlockerContextProps {
  setShouldBlockNavigation: (key: string, options: BlockerOptions) => void;
  clearNavigationBlocker: (key: string) => void;
}

export const NavigationBlockerContext = React.createContext<NavigationBlockerContextProps>({} as NavigationBlockerContextProps);

export interface NextLocation {
  pathname: string;
  search?: Record<string, any>;
  params?: Record<string, any>;
  [key: string]: any;
}

export interface BlockerOptions {
  isDirty: boolean;
  onDiscard?: (next?: NextLocation) => void;
  onConfirm?: (next?: NextLocation) => void | Promise<void>;
  modalContent?: ICustomModalProps;
}

export interface ICustomModalProps {
  title?: string;
  body?: ReactNode;
  discardText?: string;
  confirmText?: string;
  cancelText?: string;
  disableConfirm?: boolean;
  discardButtonProps?: MuiButtonProps;
}

interface INavigationBlockerModal extends ICustomModalProps {
  open: boolean;
  onClose: () => void;
  onDiscard?: () => void;
  onConfirm?: () => void | Promise<void>;
}

export const NavigationBlockerModal = ({
  onDiscard,
  onClose,
  onConfirm,
  open = false,
  title = 'Unsaved Changes',
  body = <div>You are about to leave this page. Your changes are unsaved.</div>,
  discardText = 'Discard',
  confirmText = 'Keep Editing',
  cancelText,
  disableConfirm = false,
  discardButtonProps,
}: INavigationBlockerModal) => {
  const footer = (
    <Box sx={{ ...flex.itemsCenter, justifyContent: 'space-between', gap: 2 }}>
      {cancelText && <Button onClick={onClose}>{cancelText}</Button>}
      <Box sx={{ ...flex.itemsCenter, gap: 2 }}>
        <Button onClick={onDiscard} variant="outlined" {...discardButtonProps}>
          {discardText}
        </Button>
        <Button variant="contained" onClick={onConfirm ?? onClose} disabled={disableConfirm}>
          {confirmText}
        </Button>
      </Box>
    </Box>
  );

  return (
    <AvDialog title={title} titleVariant="warning" onClose={onClose} footer={footer} open={open} size="md">
      {body}
    </AvDialog>
  );
};

export const NavigationBlockerProvider = ({ children }: React.PropsWithChildren = {}) => {
  const [blockers, setBlockers] = useState<Record<string, BlockerOptions>>({});
  const hasBlockers = useMemo(() => Object.values(blockers).some(({ isDirty }) => isDirty), [blockers]);
  const globalNotFound = useMatch({ strict: false, select: ({ globalNotFound }) => globalNotFound });

  const { proceed, reset, status, next } = useBlocker({
    shouldBlockFn: () => hasBlockers,
    // TODO: @tanstack/react-router bug, remove when the following issue is resolved: https://github.com/TanStack/router/issues/3761
    enableBeforeUnload: hasBlockers,
    withResolver: true,
    disabled: globalNotFound,
  } as const);

  const setShouldBlockNavigation = useCallback((key: string, value: BlockerOptions) => {
    setBlockers(prev => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const clearNavigationBlocker = useCallback((key: string) => {
    setBlockers(prev => {
      const { [key]: blocker, ...rest } = prev;
      return rest;
    });
  }, []);

  const handleBlockersUnload = useCallback(() => {
    Object.values(blockers).forEach(({ isDirty, onDiscard }) => {
      if (isDirty && onDiscard) {
        onDiscard();
      }
    });
    setBlockers({});
  }, [blockers]);

  useEffect(() => {
    window.addEventListener('unload', handleBlockersUnload);

    return () => {
      window.removeEventListener('unload', handleBlockersUnload);
    };
  }, [handleBlockersUnload]);

  const { activeBlocker, activeKey } = useMemo(() => {
    const entry = Object.entries(blockers).findLast(([, value]) => value.isDirty);
    return { activeBlocker: entry?.[1], activeKey: entry?.[0] };
  }, [blockers]);
  const value = useMemo(() => ({ setShouldBlockNavigation, clearNavigationBlocker }), [setShouldBlockNavigation, clearNavigationBlocker]);

  return (
    <NavigationBlockerContext.Provider value={value}>
      <NavigationBlockerModal
        {...(activeBlocker?.modalContent ?? {})}
        onClose={reset ?? noop}
        open={status === 'blocked'}
        onDiscard={() => {
          if (activeKey) {
            activeBlocker?.onDiscard?.(next);
            clearNavigationBlocker(activeKey);
          }
          proceed?.();
        }}
        onConfirm={async () => {
          if (activeKey && activeBlocker?.onConfirm) {
            await activeBlocker.onConfirm(next);
            clearNavigationBlocker(activeKey);
            proceed?.();
          } else {
            reset?.();
          }
        }}
      />
      {children}
    </NavigationBlockerContext.Provider>
  );
};
